import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { Calendar, Clock, MapPin, User, Phone, FileText, Plus, Edit, Trash2, Check, X, Wifi, WifiOff } from 'lucide-react';
import AppointmentForm from './AppointmentForm';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import EmptyState from '../../../components/ui/EmptyState';
import { subscribePooled } from '../../../utils/realtimePoolManager';

// ─── Status toast ─────────────────────────────────────────────────────────────

const STATUS_TOAST_META = {
  confirmed: { label: 'Rendez-vous confirmé', bg: 'bg-green-50 border-green-200', text: 'text-green-800', icon: '✅' },
  rescheduled: { label: 'Rendez-vous reprogrammé', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', icon: '🔄' },
  cancelled: { label: 'Rendez-vous annulé', bg: 'bg-red-50 border-red-200', text: 'text-red-800', icon: '❌' },
  completed: { label: 'Rendez-vous terminé', bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800', icon: '🏁' },
  scheduled: { label: 'Nouveau rendez-vous planifié', bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800', icon: '📅' },
};

const RealtimeToast = ({ toasts, onDismiss }) => {
  if (!toasts?.length) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {toasts?.map((t) => {
        const meta = STATUS_TOAST_META?.[t?.status] || { label: 'Mise à jour', bg: 'bg-gray-50 border-gray-200', text: 'text-gray-800', icon: '🔔' };
        return (
          <div
            key={t?.id}
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg pointer-events-auto animate-fade-in ${meta?.bg}`}
          >
            <span className="text-lg leading-none mt-0.5">{meta?.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${meta?.text}`}>{meta?.label}</p>
              {t?.title && <p className="text-xs text-gray-600 truncate mt-0.5">{t?.title}</p>}
            </div>
            <button
              onClick={() => onDismiss?.(t?.id)}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 pointer-events-auto"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

const AppointmentManagement = ({ onStatsUpdate }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef({});
  const prevStatusMap = useRef({});

  const addToast = useCallback((toast) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { ...toast, id }]?.slice(-4));
    const timer = setTimeout(() => {
      setToasts((prev) => prev?.filter((t) => t?.id !== id));
      delete timerRefs?.current?.[id];
    }, 5000);
    timerRefs.current[id] = timer;
  }, []);

  const dismissToast = useCallback((id) => {
    if (timerRefs?.current?.[id]) {
      clearTimeout(timerRefs?.current?.[id]);
      delete timerRefs?.current?.[id];
    }
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase?.from('appointments')?.select(`
          *,
          property:properties(title, address, property_type, price),
          client:user_profiles!appointments_client_id_fkey(full_name, email, phone),
          agent:user_profiles!appointments_agent_id_fkey(full_name, email, phone),
          notary:notaries(
            id,
            license_number,
            office_address,
            user:user_profiles(full_name, email, phone)
          )
        `);

      if (filterStatus !== 'all') {
        query = query?.eq('status', filterStatus);
      }

      const { data, error } = await query?.order('scheduled_date', { ascending: true });

      if (error) throw error;

      // Update prevStatusMap for change detection
      const newMap = {};
      (data || [])?.forEach((a) => { newMap[a?.id] = a?.status; });
      prevStatusMap.current = newMap;

      setAppointments(data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  // Initial fetch + real-time subscription
  useEffect(() => {
    fetchAppointments();

    const unsubscribe = subscribePooled({
      channelName: 'appointment-management-realtime',
      listeners: [
        { event: 'INSERT', schema: 'public', table: 'appointments' },
        { event: 'UPDATE', schema: 'public', table: 'appointments' },
        { event: 'DELETE', schema: 'public', table: 'appointments' },
      ],
      debounceMs: 300,
      onStatusChange: (status) => setIsLive(status === 'SUBSCRIBED'),
      callback: (batch) => {
        // Detect status changes for toasts before re-fetching
        batch?.forEach((payload) => {
          if (payload?.eventType === 'UPDATE' && payload?.new) {
            const prev = prevStatusMap?.current?.[payload?.new?.id];
            const next = payload?.new?.status;
            if (prev && next && prev !== next) {
              addToast({ status: next, title: payload?.new?.appointment_type || '' });
            }
          } else if (payload?.eventType === 'INSERT' && payload?.new) {
            addToast({ status: 'scheduled', title: payload?.new?.appointment_type || 'Nouveau rendez-vous' });
          }
        });
        fetchAppointments();
        onStatsUpdate?.();
      },
    });

    return () => {
      Object.values(timerRefs?.current ?? {})?.forEach(clearTimeout);
      timerRefs.current = {};
      unsubscribe();
    };
  }, [fetchAppointments, onStatsUpdate, addToast]);

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      const { error } = await supabase?.from('appointments')?.update({
        status: newStatus,
        updated_at: new Date()?.toISOString()
      })?.eq('id', appointmentId);

      if (error) throw error;
      // Real-time subscription will trigger fetchAppointments automatically
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error updating appointment status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) return;

    try {
      const { error } = await supabase?.from('appointments')?.delete()?.eq('id', appointmentId);

      if (error) throw error;
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleAppointmentSaved = async () => {
    setShowForm(false);
    setSelectedAppointment(null);
    onStatsUpdate?.();
  };

  const statusColors = {
    scheduled: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    rescheduled: 'bg-purple-100 text-purple-800'
  };

  const statusLabels = {
    scheduled: 'Programmé',
    confirmed: 'Confirmé',
    completed: 'Terminé',
    cancelled: 'Annulé',
    rescheduled: 'Reporté'
  };

  const isUpcoming = (date) => new Date(date) > new Date();
  const isPast = (date) => new Date(date) < new Date();

  return (
    <div className="space-y-6">
      {/* Real-time toasts */}
      <RealtimeToast toasts={toasts} onDismiss={dismissToast} />

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">Gestion des Rendez-vous</h2>
            {isLive ? (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full">
                <Wifi className="h-3 w-3 text-green-500" />
                <span className="text-[10px] font-semibold text-green-600">Live</span>
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full">
                <WifiOff className="h-3 w-3 text-gray-400" />
                <span className="text-[10px] text-gray-400">Connexion…</span>
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Coordonnez les rendez-vous entre clients, agents et notaires avec détection des conflits
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedAppointment(null);
            setShowForm(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Nouveau Rendez-vous</span>
        </button>
      </div>
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Filtrer par statut:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="ml-2 px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="scheduled">Programmés</option>
              <option value="confirmed">Confirmés</option>
              <option value="completed">Terminés</option>
              <option value="cancelled">Annulés</option>
              <option value="rescheduled">Reportés</option>
            </select>
          </div>
          <div className="text-sm text-gray-600">
            <span>{appointments?.length} rendez-vous trouvé(s)</span>
          </div>
        </div>
      </div>
      {/* Appointments List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des rendez-vous...</p>
          </div>
        ) : appointments?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border">
            <EmptyState
              iconName="CalendarOff"
              title="Aucun rendez-vous trouvé"
              description="Aucun rendez-vous ne correspond au filtre sélectionné pour le moment."
            />
          </div>
        ) : (
          appointments?.map((appointment) => (
            <div
              key={appointment?.id}
              className={`bg-white p-6 rounded-lg shadow-sm border transition-all hover:shadow-md ${
                isPast(appointment?.scheduled_date) && appointment?.status === 'scheduled' ? 'border-l-4 border-l-red-400'
                  : isUpcoming(appointment?.scheduled_date) && appointment?.status === 'confirmed' ? 'border-l-4 border-l-green-400' : ''
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex-1 space-y-3">
                  {/* Appointment Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {appointment?.appointment_type}
                        </h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors?.[appointment?.status] || 'bg-gray-100 text-gray-800'}`}>
                          {statusLabels?.[appointment?.status] || appointment?.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Propriété: {appointment?.property?.title}
                      </p>
                    </div>
                  </div>

                  {/* Appointment Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">
                        {format(new Date(appointment?.scheduled_date), 'EEEE d MMMM yyyy', { locale: fr })}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>
                        {format(new Date(appointment?.scheduled_date), 'HH:mm')}
                        ({appointment?.duration_minutes} min)
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="truncate">
                        {appointment?.location || appointment?.property?.address}
                      </span>
                    </div>
                  </div>

                  {/* Participants */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="font-medium text-gray-700 mb-1">Client:</div>
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span>{appointment?.client?.full_name}</span>
                      </div>
                      {appointment?.client?.phone && (
                        <div className="flex items-center space-x-2 mt-1">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span>{appointment?.client?.phone}</span>
                        </div>
                      )}
                    </div>

                    {appointment?.agent && (
                      <div>
                        <div className="font-medium text-gray-700 mb-1">Agent:</div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span>{appointment?.agent?.full_name}</span>
                        </div>
                        {appointment?.agent?.phone && (
                          <div className="flex items-center space-x-2 mt-1">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <span>{appointment?.agent?.phone}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {appointment?.notary && (
                      <div>
                        <div className="font-medium text-gray-700 mb-1">Notaire:</div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span>{appointment?.notary?.user?.full_name}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {appointment?.notary?.license_number}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {appointment?.notes && (
                    <div className="flex items-start space-x-2">
                      <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-700 text-sm">Notes:</div>
                        <p className="text-sm text-gray-600">{appointment?.notes}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-2 lg:ml-4">
                  {appointment?.status === 'scheduled' && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleStatusUpdate(appointment?.id, 'confirmed')}
                        className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm"
                      >
                        <Check className="h-4 w-4" />
                        <span>Confirmer</span>
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(appointment?.id, 'cancelled')}
                        className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm"
                      >
                        <X className="h-4 w-4" />
                        <span>Annuler</span>
                      </button>
                    </div>
                  )}

                  {appointment?.status === 'confirmed' && (
                    <button
                      onClick={() => handleStatusUpdate(appointment?.id, 'completed')}
                      className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm"
                    >
                      <Check className="h-4 w-4" />
                      <span>Marquer terminé</span>
                    </button>
                  )}

                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setShowForm(true);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <Edit className="h-4 w-4" />
                      <span>Modifier</span>
                    </button>
                    <button
                      onClick={() => handleDeleteAppointment(appointment?.id)}
                      className="flex items-center space-x-1 px-3 py-1 text-red-600 hover:text-red-800 text-sm"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Appointment Form Modal */}
      {showForm && (
        <AppointmentForm
          appointment={selectedAppointment}
          onSave={handleAppointmentSaved}
          onCancel={() => {
            setShowForm(false);
            setSelectedAppointment(null);
          }}
        />
      )}
    </div>
  );
};

export default AppointmentManagement;